import React from 'react';
import { motion } from 'framer-motion';
import './Testimonials.css';

type TestimonialProps = {
  quote: string;
  name: string;
  title: string;
  image?: string;
};

const Testimonial: React.FC<TestimonialProps> = ({ quote, name, title, image }) => {
  return (
    <motion.div 
      className="testimonial-card"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
    >
      <div className="testimonial-content">
        <blockquote>"{quote}"</blockquote>
        <div className="testimonial-author">
          {image && (
            <div className="testimonial-avatar">
              <img src={image} alt={name} />
            </div>
          )}
          <div className="testimonial-info">
            <h4>{name}</h4>
            <p>{title}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Testimonials: React.FC = () => {
  const testimonialData: TestimonialProps[] = [
    {
      quote: "I had no idea how to approach the Work & Activities section or how to frame my experiences. Nafis helped me organize my application, strengthen my writing, and highlight the experiences that mattered most. His feedback gave me the confidence to submit my application, and I was thrilled to receive multiple interview invitations and an acceptance to one of my top-choice medical schools.",
      name: "Kian",
      title: "Accepted to University of Colorado School of Medicine",
    },
    {
      quote: "I've always struggled with writing, but Nafis made the entire application process approachable and encouraging. He helped me brainstorm ideas and refine my personal statement and secondary essays with thoughtful, detailed feedback. I'm incredibly proud of the application I submitted, and I was fortunate to receive multiple acceptances, including one of my dream schools.",
      name: "Isabella",
      title: "Accepted to Case Western Reserve University School of Medicine",
    },
    {
      quote: "Nafis is an outstanding mentor for premed students applying to medical school. He supported me through every step of the application process from insightful feedback, essay editing, and counseling. His deep knowledge of the application process is unique, and I am incredibly grateful for his help.",
      name: "Rohan",
      title: "Accepted to University of Arizona College of Medicine",
    },
    {
      quote: "Nafis has been a one of a kind resource throughout my medical school application process. His support ranged from strategic planning and personal statement editing to honest, constructive advice that kept me on track. He brings a level of experience and care that's hard to find, and I'm incredibly thankful for everything he's done to help me succeed.",
      name: "Priya",
      title: "Accepted to Northwestern University Feinberg School of Medicine",
    },
    {
      quote: "I got a 502 on the MCAT and thought I had no chance at getting in to MD schools, but Nafis helped me develop the strongest possible application by highlighting my strengths, experiences, and personal story. Thanks to his guidance, I was accepted to 2 MD schools!",
      name: "Brandon",
      title: "Accepted to University of Arizona College of Medicine",
    },
    {
      quote: "After not getting in my first cycle, I worked with Nafis to revise every part of my application. His feedback was honest, detailed, and incredibly supportive. This cycle, I received multiple interview invites and was accepted to two MD programs, including my top choice!",
      name: "Lauren",
      title: "Accepted to The Ohio State University College of Medicine",
    },
  ];

  return (
    <section id="testimonials" className="testimonials-section">
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <h2>What Our Students Say</h2>
          <p>Success stories from students we've helped achieve their medical school dreams</p>
        </motion.div>
        
        <div className="testimonials-grid">
          {testimonialData.map((testimonial, index) => (
            <Testimonial 
              key={index}
              quote={testimonial.quote}
              name={testimonial.name}
              title={testimonial.title}
              image={testimonial.image}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
